import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import {
  Sparkles, TrendingUp, TrendingDown, Users, Target, Clock,
  AlertTriangle, CheckCircle2, Loader2, Brain, Zap, Award, BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CRMAIInsights() {
  const { token } = useCRMAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [insights, setInsights] = useState(null);

  useEffect(() => { fetchInsights(); }, [period]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/ai/insights?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setInsights(await res.json());
    } catch (err) { console.error('Failed to fetch insights:', err); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="relative">
            <Brain className="w-16 h-16 text-indigo-600 animate-pulse" />
            <Sparkles className="w-6 h-6 text-amber-500 absolute -top-1 -right-1 animate-bounce" />
          </div>
          <p className="mt-4 text-slate-600">Analyzing your data...</p>
        </div>
      </CRMLayout>
    );
  }

  const data = insights || { productivity: {}, alerts: [], recommendations: [], top_performers: [], underperformers: [] };

  return (
    <CRMLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">AI Insights</h1>
            <p className="text-slate-500">Smart analytics & recommendations</p>
          </div>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Productivity Score */}
      <Card className="p-6 mb-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-90">Overall Productivity Score</p>
            <p className="text-5xl font-bold mt-2">{data.productivity?.score || 0}%</p>
            <div className="flex items-center gap-2 mt-2">
              {(data.productivity?.trend || 0) >= 0 ? (
                <><TrendingUp className="w-4 h-4" /><span className="text-sm">{data.productivity?.trend}% vs last period</span></>
              ) : (
                <><TrendingDown className="w-4 h-4" /><span className="text-sm">{Math.abs(data.productivity?.trend)}% vs last period</span></>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 mb-2"><Target className="w-5 h-5 opacity-70" /><span>{data.productivity?.tasks_completed || 0} Tasks Done</span></div>
            <div className="flex items-center gap-2 mb-2"><Clock className="w-5 h-5 opacity-70" /><span>{data.productivity?.items_delivered || 0} Items Delivered</span></div>
            <div className="flex items-center gap-2"><Users className="w-5 h-5 opacity-70" /><span>{data.productivity?.active_members || 0} Active Members</span></div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Alerts */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-slate-900">Alerts & Issues</h3>
          </div>
          {(!data.alerts || data.alerts.length === 0) ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <p className="text-slate-600">All good! No issues detected</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.alerts.map((alert, idx) => (
                <div key={idx} className={`p-3 rounded-lg border-l-4 ${alert.severity === 'high' ? 'bg-red-50 border-red-500' : alert.severity === 'medium' ? 'bg-amber-50 border-amber-500' : 'bg-blue-50 border-blue-500'}`}>
                  <p className="font-medium text-slate-900">{alert.title}</p>
                  <p className="text-sm text-slate-600">{alert.message}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Recommendations */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-slate-900">AI Recommendations</h3>
          </div>
          {(!data.recommendations || data.recommendations.length === 0) ? (
            <p className="text-slate-500 text-center py-6">No recommendations at this time</p>
          ) : (
            <div className="space-y-3">
              {data.recommendations.map((rec, idx) => (
                <div key={idx} className="p-3 bg-indigo-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xs font-bold">{idx + 1}</div>
                    <div>
                      <p className="font-medium text-slate-900">{rec.title}</p>
                      <p className="text-sm text-slate-600">{rec.action}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-slate-900">Top Performers</h3>
          </div>
          {(!data.top_performers || data.top_performers.length === 0) ? (
            <p className="text-slate-500 text-center py-6">No data available</p>
          ) : (
            <div className="space-y-4">
              {data.top_performers.map((emp, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : 'bg-amber-700'}`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-slate-900">{emp.name}</span>
                      <span className="text-sm text-slate-500">{emp.items} items</span>
                    </div>
                    <Progress value={emp.score || 0} className="h-2" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Needs Attention */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-slate-500" />
            <h3 className="font-semibold text-slate-900">Needs Attention</h3>
          </div>
          {(!data.underperformers || data.underperformers.length === 0) ? (
            <div className="text-center py-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <p className="text-slate-600">Everyone is performing well!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.underperformers.map((emp, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{emp.name}</p>
                      <p className="text-sm text-slate-500">{emp.issue}</p>
                    </div>
                    <Badge variant="outline" className="text-amber-600">{emp.items} items</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </CRMLayout>
  );
}
